import { Injectable } from '@angular/core';
import { WebSocketService } from '../../../services/websocket.service';
import { Routine } from 'app/modules/admin/models/routines.types';
import { User } from 'app/modules/admin/models/users.types';
import { firstValueFrom, take } from 'rxjs';
import Swal from 'sweetalert2';
import {
    RoutinePayload,
    WSRoutineResponse
} from '../types/routine-detonator.types';
import { generateExecutionId, extractErrorMessage } from '../utils/routine-detonator.utils';
import { RoutineDetonatorStateService } from './routine-detonator-state.service';

/**
 * Servicio para manejar la ejecución de rutinas mediante WebSocket
 */
@Injectable()
export class RoutineExecutionService {
    constructor(private wsService: WebSocketService, private stateService: RoutineDetonatorStateService) { }

    /**
     * Envía rutinas al WebSocket para detonarlas
     */
    async executeRoutines(
        routines: Routine[],
        allRoutines: Routine[],
        players: User[]
    ): Promise<void> {
        if (routines.length === 0) {
            Swal.fire({
                title: 'Error',
                text: 'No hay rutinas listas para enviar.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Preparar payload
        const payload = routines.map(r => {
            // Usar el execution_id que ya viene en la rutina (generado en el componente)
            const execution_id = r.execution_id || generateExecutionId(r.id, r.selected_user_id);

            return this.createRoutinePayload(r, execution_id, players);
        });

        const instruction = { type: 'routine', payload };
        this.wsService.sendMessage(instruction);

        try {
            const response: WSRoutineResponse = await firstValueFrom(
                this.wsService.messages$.pipe(take(1))
            );

            if (response.meta.type === 'routine_response') {
                this.handleExecutionResponse(response, players, allRoutines);
            }
        } catch (error) {
            console.error('Error al procesar la respuesta del WS:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo enviar la información al servidor.',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    }

    /**
     * Crea el payload para una rutina
     */
    private createRoutinePayload(
        routine: Routine,
        execution_id: string,
        players: User[]
    ): RoutinePayload {
        return {
            execution_id: execution_id,
            user_id: routine.selected_user_id,
            routine_id: routine.id,
            division_id: players.find(p => p.id === routine.selected_user_id)?.division_id ?? null,
            relationDevices: routine.cluster.type_devices
                .filter(td => !!td.selected_device_id)
                .map(td => ({
                    id: td.pivot.id,
                    device_id: td.selected_device_id
                }))
        };
    }

    /**
     * Maneja la respuesta de ejecución del WebSocket
     */
    private handleExecutionResponse(
        response: WSRoutineResponse,
        players: User[],
        allRoutines: Routine[]
    ): void {
        const failed = response.data.results.filter(r => !r.success);
        const successful = response.data.results.filter(r => r.success);

        // Log detallado para debugging
        console.log('Respuesta del servidor:', {
            successful: successful.length,
            failed: failed.length,
            results: response.data.results
        });

        // Mostrar un único mensaje con éxitos y errores
        this.showCombinedMessage(successful, failed, players, allRoutines);
    }

    /**
     * Muestra mensaje combinado con rutinas exitosas y fallidas
     */
    private showCombinedMessage(successful: any[], failed: any[], players: User[], allRoutines: Routine[]): void {
        let htmlContent = '';
        let title = '';
        let icon: 'success' | 'warning' | 'error' = 'success';

        // Determinar título e ícono según los resultados
        if (successful.length > 0 && failed.length === 0) {
            title = 'Correcto';
            icon = 'success';
        } else if (successful.length === 0 && failed.length > 0) {
            title = 'Error';
            icon = 'error';
        } else {
            title = 'Resultado de Ejecución';
            icon = 'warning';
        }

        // Sección de éxitos
        if (successful.length > 0) {
            const successDetails = successful
                .map(r => {
                    const playerName = players.find(p => p.id === r.originalRequest.user_id)?.name || 'Jugador desconocido';
                    // Actualizar routine con execution_id con el valor de routineLogId
                    this.stateService.updateRoutineByExecutionId(r.originalRequest.execution_id, routine => ({
                        ...routine,
                        routine_log_id: r.routineLogId
                    }));
                    return `• ${playerName}`;
                })
                .join('<br>');

            htmlContent += `
                <div style="text-align: left; margin-bottom: ${failed.length > 0 ? '20px' : '0'};">
                    <strong style="color: #10b981; font-size: 16px;">✅ Rutinas iniciadas correctamente (${successful.length}):</strong>
                    <div style="margin-top: 10px; padding-left: 10px;">
                        ${successDetails}
                    </div>
                </div>
            `;
        }

        // Sección de errores
        if (failed.length > 0) {
            console.log('Rutinas fallidas:', failed);

            const failedDetails = failed
                .map(r => {
                    const errorMsg = extractErrorMessage(r.error);
                    const playerName = players.find(p => p.id === r.originalRequest.user_id)?.name || 'Jugador desconocido';
                    return `• ${playerName}: <span style="color: #ef4444;">${errorMsg}</span>`;
                })
                .join('<br>');

            htmlContent += `
                <div style="text-align: left;">
                    <strong style="color: #ef4444; font-size: 16px;">❌ Rutinas con errores (${failed.length}):</strong>
                    <div style="margin-top: 10px; padding-left: 10px;">
                        ${failedDetails}
                    </div>
                </div>
            `;
        }

        Swal.fire({
            title: title,
            html: htmlContent,
            icon: icon,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3b82f6',
            width: '600px',
            customClass: {
                confirmButton: 'swal2-confirm-custom',
                htmlContainer: 'swal2-html-container-left'
            }
        });
    }

    /**
     * Muestra mensaje de éxito (DEPRECATED - Usar showCombinedMessage)
     */
    private showSuccessMessage(successful: any[], players: User[]): void {
        const successDetails = successful
            .map(r => {
                const playerName = players.find(p => p.id === r.originalRequest.user_id)?.name || 'Jugador desconocido';
                // Actualizar routine con execution_id con el valor de routineLogId
                this.stateService.updateRoutineByExecutionId(r.originalRequest.execution_id, routine => ({
                    ...routine,
                    routine_log_id: r.routineLogId
                }));
                return `• ${playerName}`;
            })
            .join('<br>');

        Swal.fire({
            title: 'Correcto',
            html: `Se iniciaron ${successful.length} rutina(s) correctamente:<br><br>${successDetails}`,
            icon: 'success',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3b82f6', // blue-500
            customClass: {
                confirmButton: 'swal2-confirm-custom'
            }
        });
    }

    /**
     * Muestra mensaje de error
     */
    private showErrorMessage(failed: any[], players: User[], allRoutines: Routine[]): void {
        const failedDetails =
            failed.map(r => {
                const errorMsg = extractErrorMessage(r.error);

                // Fallback si no se encuentra en el mapa
                const routineName =
                    allRoutines.find(rt => rt.id === r.originalRequest.routine_id)?.name || `ID: ${r.originalRequest.routine_id}`;
                const playerName = players.find(p => p.id === r.originalRequest.user_id)?.name || 'Jugador desconocido';
                return `• ${routineName} (${playerName}): ${errorMsg}`;
            })
                .join('<br>');

        Swal.fire({
            title: 'Algunas fallaron',
            html: failedDetails,
            icon: 'warning',
            confirmButtonText: 'Aceptar'
        });
    }
}
